/**
 * Hook to gather context about the current page for Astraea AI
 * This helps Astraea understand what the user is viewing and provide relevant assistance
 */
import { useLocation } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import { usePresence } from '../context/PresenceContext';
import api from '../services/api';

// Page context registry - components can register additional context
let pageContextData = {};

export const setPageContext = (key, value) => {
  pageContextData[key] = value;
};

export const clearPageContext = (key) => {
  if (key) {
    delete pageContextData[key];
  } else {
    pageContextData = {};
  }
};

const usePageContext = () => {
  const location = useLocation();
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(false);
  const { emailUnread } = usePresence() || {};

  // Map routes to page types
  const getPageType = useCallback((pathname) => {
    if (pathname === '/') return 'dashboard';
    if (pathname.startsWith('/cases')) return 'cases';
    if (pathname.startsWith('/mail')) return 'mail';
    if (pathname.startsWith('/contacts')) return 'contacts';
    if (pathname.startsWith('/documents')) return 'documents';
    if (pathname.startsWith('/calendar')) return 'calendar';
    if (pathname.startsWith('/invoices')) return 'invoices';
    if (pathname.startsWith('/sigil')) return 'sigil';
    if (pathname.startsWith('/triage')) return 'triage';
    if (pathname.startsWith('/intake')) return 'intake';
    if (pathname.startsWith('/accounting')) return 'accounting';
    if (pathname.startsWith('/payroll')) return 'payroll';
    if (pathname.startsWith('/admin')) return 'admin';
    if (pathname.startsWith('/settings')) return 'settings';
    if (pathname.startsWith('/profile')) return 'profile';
    if (pathname.startsWith('/voip')) return 'phone';
    if (pathname.startsWith('/signatures')) return 'signatures';
    if (pathname.startsWith('/law-library')) return 'law-library';
    return 'other';
  }, []);

  // Get human-readable page name
  const getPageName = useCallback((pageType) => {
    const names = {
      dashboard: 'Dashboard',
      cases: 'Cases',
      mail: 'OuijaMail',
      contacts: 'Contacts',
      documents: 'Documents',
      calendar: 'Calendar',
      invoices: 'Invoices',
      sigil: 'Sigil',
      triage: 'Triage Queue',
      intake: 'Client Intake',
      accounting: 'Accounting',
      payroll: 'Payroll',
      admin: 'Admin Panel',
      settings: 'Settings',
      profile: 'User Profile',
      phone: 'Planchette',
      signatures: 'E-Signatures',
      'law-library': 'Law Library',
      other: 'Application'
    };
    return names[pageType] || 'Application';
  }, []);

  // Fetch additional context based on page type
  const fetchPageContext = useCallback(async (pageType) => {
    setLoading(true);

    try {
      let additionalContext = {};

      switch (pageType) {
        case 'dashboard':
          // Get quick stats
          try {
            const [triageRes, calendarRes] = await Promise.all([
              api.get('/triage/stats').catch(() => ({ data: null })),
              api.get('/calendar/events?limit=3').catch(() => ({ data: [] }))
            ]);
            additionalContext = {
              triageStats: triageRes.data,
              upcomingEvents: Array.isArray(calendarRes.data) ? calendarRes.data.slice(0, 3) : []
            };
          } catch (e) {
            console.log('Could not fetch dashboard context');
          }
          break;

        case 'cases':
          // If viewing a specific case, that info would be in pageContextData
          break;

        case 'mail':
          // Read email stats from PresenceContext (no duplicate API call)
          if (emailUnread) {
            additionalContext = {
              emailStats: emailUnread
            };
          }
          break;

        case 'triage':
          // Get triage stats
          try {
            const triageRes = await api.get('/triage/stats');
            additionalContext = {
              triageStats: triageRes.data
            };
          } catch (e) {
            console.log('Could not fetch triage context');
          }
          break;

        case 'calendar':
          // Get today's events
          try {
            const now = new Date();
            const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
            const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
            const calRes = await api.get(`/calendar/events?start_date=${startOfDay.toISOString()}&end_date=${endOfDay.toISOString()}`);
            additionalContext = {
              todaysEvents: calRes.data
            };
          } catch (e) {
            console.log('Could not fetch calendar context');
          }
          break;

        default:
          break;
      }

      // Merge with any component-registered context
      const fullContext = {
        pageType,
        pageName: getPageName(pageType),
        path: location.pathname,
        ...additionalContext,
        ...pageContextData
      };

      setContext(fullContext);
    } catch (error) {
      console.error('Error fetching page context:', error);
      setContext({
        pageType,
        pageName: getPageName(pageType),
        path: location.pathname,
        ...pageContextData
      });
    } finally {
      setLoading(false);
    }
  }, [location.pathname, getPageName]);

  // Update context when location changes
  useEffect(() => {
    const pageType = getPageType(location.pathname);
    fetchPageContext(pageType);
  }, [location.pathname, getPageType, fetchPageContext]);

  // Format context for AI consumption
  const getContextSummary = useCallback(() => {
    if (!context) return '';

    let summary = `User is currently viewing: ${context.pageName}`;

    // Add specific context based on page
    if (context.currentCase) {
      summary += `\nViewing Case: ${context.currentCase.case_name} (${context.currentCase.case_number})`;
      if (context.currentCase.status) {
        summary += ` - Status: ${context.currentCase.status}`;
      }
      if (context.currentCase.client_name) {
        summary += ` - Client: ${context.currentCase.client_name}`;
      }
    }

    if (context.currentContact) {
      summary += `\nViewing Contact: ${context.currentContact.name}`;
      if (context.currentContact.email) {
        summary += ` (${context.currentContact.email})`;
      }
    }

    if (context.currentEmail) {
      summary += `\nViewing Email from: ${context.currentEmail.from}`;
      summary += `\nSubject: ${context.currentEmail.subject}`;
    }

    if (context.currentDocument) {
      summary += `\nViewing Document: ${context.currentDocument.name}`;
    }

    if (context.emailStats) {
      summary += `\nEmail Stats: ${context.emailStats.unread_count || 0} unread`;
      if (context.emailStats.urgent_count) {
        summary += `, ${context.emailStats.urgent_count} urgent`;
      }
    }

    if (context.triageStats) {
      summary += `\nTriage: ${context.triageStats.pending || 0} pending actions`;
      if (context.triageStats.overdue) {
        summary += `, ${context.triageStats.overdue} overdue`;
      }
    }

    if (context.todaysEvents && context.todaysEvents.length > 0) {
      summary += `\nToday's Events: ${context.todaysEvents.length} scheduled`;
    }

    if (context.upcomingEvents && context.upcomingEvents.length > 0) {
      summary += `\nUpcoming: ${context.upcomingEvents.map(e => e.title).join(', ')}`;
    }

    return summary;
  }, [context]);

  return {
    context,
    loading,
    getContextSummary,
    pageType: context?.pageType,
    pageName: context?.pageName
  };
};

export default usePageContext;
